import {observer} from "mobx-react-lite";
import {Authorization} from "../../components/Authorization";
import {AppPermissions} from "../../utilities/Constants";
import {useStores} from "../../index";
import {useEffect, useMemo, useState} from "react";
import {ClientsTableColumns, ClientsTableFilterInstance} from "./ClientsTableColumns";
import {ClientType, OrganizationClient} from "../../types/models/OrganizationClient";
import {AppButton} from "../../components/form";
import {t} from "../../utilities/TranslationUtils";
import {AppConfirmDialog, AppPageHeader, AppSectionErrorBoundary, AppSpacer, AppUseIcon} from "../../components/ui";
import {Button, TableCell} from "@trackman/web-shared-components";
import {StaticTableWithFilters} from "../../components/Tables/StaticTableWithFilters";
import ClientsAddDialog from "./ClientsAddDialog";
import SecretDisplayDialog from "./SecretDisplayDialog";
import {ErrorWrapper} from "../Forbidden/Error";
import {Feature} from "../../types/ProfileTypes";

const ClientsPage: React.FunctionComponent = observer(() => {
  const { profileStore, clientStore, teamPortalApiAxiosInstance } = useStores();
  const { isLoadingData, clients, isAddingClient } = clientStore;

  const [isAddClientsDialogOpened, setIsAddClientsDialogOpened] = useState(false);
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState(false);
  const [isRegenerateConfirmationVisible, setIsRegenerateConfirmationVisible] = useState(false);
  const [isSecretDialogVisible, setIsSecretDialogVisible] = useState(false);
  const [currClientId, setCurrClientId] = useState('');
  const [currName, setCurrName] = useState('');
  const [secret, setSecret] = useState('');
  const [isClientAdded, setIsClientAdded] = useState(false);

  const rows = useMemo(() => clients, [clients]);
  const columns = useMemo(() => ClientsTableColumns(clients), [clients]);

  useEffect(() => {
    clientStore.LoadData(teamPortalApiAxiosInstance);
  }, []);

  const addClientHandler = (data) => {
    if (isLoadingData) return;
    clientStore.AddClient(
      teamPortalApiAxiosInstance,
      {
        id: data.id,
        name: data.name,
        clientType: ClientType.dataApi,
      },
      (result: string) => {
        setSecret(result);
        setIsClientAdded(true);
      },
      () => {
        setIsClientAdded(false);
      }
    );
  };

  const closeAddClientDialog = () => {
    setIsAddClientsDialogOpened(false);
    setSecret('');
    if (isClientAdded) {
      clientStore.LoadData(teamPortalApiAxiosInstance);
    }
    setIsClientAdded(false);
  };

  const deleteClientHandler = () => {
    if (isLoadingData) return;
    clientStore.DeleteClient(
      teamPortalApiAxiosInstance,
      currClientId,
      () => {},
      () => {}
    );
    setIsDeleteConfirmationVisible(false);
  };
  const regenerateClientSecretHandler = () => {
    if (isLoadingData) return;
    clientStore.RegenerateClientSecret(
      teamPortalApiAxiosInstance,
      currClientId,
      (result: string) => {
        setSecret(result);
        setIsSecretDialogVisible(true);
      },
      () => {}
    );
    setIsRegenerateConfirmationVisible(false);
  };

  const showRegenerateClientSecretDialog = (clientId: string, name: string) => {
    setCurrClientId(clientId);
    setCurrName(name);
    setIsRegenerateConfirmationVisible(true);
  };
  const showDeleteClientDialog = (clientId: string) => {
    setCurrClientId(clientId);
    setIsDeleteConfirmationVisible(true);
  };

  const renderCell = (
    {
      cellValue,
      columnHeader,
      columnId,
      rowData,
    }: {
    cellValue: string;
    columnHeader: string;
    columnId: string;
    rowData: any;
  }) => {
    if (columnId === 'actions') {
      return (
        <div className='d-flex justify-content-left align-content-baseline'>
          <AppButton
            variant='transparent'
            fontFamily='lato'
            fontSize='15'
            fontWeight='400'
            className='d-flex align-items-center justify-content-center text-underline no-text-transform px-0 py-0'
            onClick={() => {
              showRegenerateClientSecretDialog(rowData.id, rowData.name);
            }}
          >
            {t('clients.regenerate')}
          </AppButton>
          <AppButton
            variant='transparent'
            onClick={() => {
              showDeleteClientDialog(rowData.id);
            }}
            className='d-flex justify-content-center align-content-baseline px-2 py-2'
          >
            <AppUseIcon name='TrashIconSecondaryGrey' width='17' height='17' color='#A0A0A0' />
          </AppButton>
        </div>
      );
    }
    return (
      <span className='overflow-ellipsis' title={`${cellValue}`}>
        {cellValue}
      </span>
    );
  };

  const rowMapper = (cells: any[]) => {
    return (
      <>
        {cells.map(({ getCellProps, column, row, render, value }) => (
          <TableCell {...getCellProps()} align={column.align}>
            {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, rowData: row.original })}
          </TableCell>
        ))}
      </>
    );
  };

  return profileStore.HasFeature(Feature.DataApiSelfService) ? (
      <AppSectionErrorBoundary>
        <div className='dataIntegrationClients-tables'>
          <AppPageHeader title={t('clients.title')}>
            <AppButton
              className='position-absolute right-0 mt-auto mb-auto'
              variant='transparent'
              disabled={isLoadingData}
              data-test='add-client-button'
              id='add-client-button'
              onClick={() =>  {
                setIsAddClientsDialogOpened(true)
                setSecret('')
              }}
            >
              {t('clients.addClient')}
              <AppUseIcon name='PlusIconWithoutBorder' width='14' height='14' className='ml-2' />
            </AppButton>
          </AppPageHeader>

          <div className='pb-4 font-13 font-400 font-lato color-darkGrey'>{t('clients.tableDescription') }</div>
          <StaticTableWithFilters<OrganizationClient>
            appTableFiltersInstance={ClientsTableFilterInstance}
            rowMapper={rowMapper}
            columns={columns}
            data={rows}
            isLoading={isLoadingData}
          />
          <ClientsAddDialog
            isLoading={isLoadingData}
            isClientAdded={isClientAdded}
            isAddingClient={isAddingClient}
            onClose={() => closeAddClientDialog()}
            onSubmit={(data: any) => addClientHandler(data)}
            isDialogOpened={isAddClientsDialogOpened}
            secret={secret}
            idPrefix={clientStore.idPrefix}
          />

          <AppConfirmDialog
            title={t('clients.deleteConfirmationTitle')}
            message={t('clients.deleteConfirmationText')}
            messageParameters={{ clientId: currClientId }}
            info={t('clients.deleteConfirmationDescription')}
            cancelLabel={t('common.cancel')}
            confirmLabel={t('clients.deleteConfirmation')}
            isDialogOpen={isDeleteConfirmationVisible}
            onConfirm={() => deleteClientHandler()}
            isLoading={false}
            onCancel={() => setIsDeleteConfirmationVisible(false)}
          />
          <AppConfirmDialog
            title={`${t('clients.regenerateConfirmationTitle')} '${currClientId}'`}
            message={t('clients.regenerateConfirmationText')}
            info={t('clients.regenerateConfirmationDescription')}
            cancelLabel={t('common.cancel')}
            confirmLabel={t('clients.generate')}
            isDialogOpen={isRegenerateConfirmationVisible}
            onConfirm={() => regenerateClientSecretHandler()}
            isLoading={false}
            onCancel={() => setIsRegenerateConfirmationVisible(false)}
          />
          <SecretDisplayDialog
            id={currClientId}
            secret={secret}
            name={currName}
            isDialogOpened={isSecretDialogVisible}
            onClose={() => setIsSecretDialogVisible(false)}
          />
        </div>
      </AppSectionErrorBoundary>
  ) : (
    <div>
      <AppPageHeader title={t('clients.licenseNotActiveMessage')}/>
      <AppSpacer space='10px'/>
      <ErrorWrapper title={t('error.missingLicenseOrFeatureTitle')} text={t('error.missingLicenseOrFeatureText')} />
    </div>
  );
});
export default Authorization(ClientsPage, AppPermissions.ManageClients);
